import React, { useState, useMemo, useEffect } from 'react';
import Overlay from '../../components/Overlay';
import ActionButton from '../../components/ActionButton';
import InlineMessage from '../../components/InlineMessage';
import useMessage from '../../lib/useMessage';
import { useTier, controlHeight } from '../../lib/tiers';
import { canSetProductionCost, canSeeInventoryCost } from '../../lib/permissions';
import { supabase } from '../../lib/supabase';
import { makeProgress } from '../../lib/branchTransfers';
import {
  recordProduction, setStandardCost, totalOnHand, valueAdded, revaluation,
  costSetter, mayChangeThisCost,
} from '../../lib/production';

// Recording that this branch made something for its own shelf.
// Issue #515, Session 79.8. Gate: docs/gates/Stede_Production_Gate_515.html,
// screen 2, decisions D31 and D32 (Option A), callouts D through G.
//
// ── What a person does with this ──
//
// Houston's fabrication manager finishes a run of 300 stakes for the rack. She
// opens the To make queue, presses Record production on the stakes row, types
// 300, and presses Record it. The shelf goes up by 300 and the stock ledger
// carries a row saying what they cost to make. Nobody is notified — that is
// gate line P1, not an omission: a branch building its own stock is not an
// event anyone else needs a bell for.
//
// ── The cost field is PRE-FILLED and EDITABLE, and both halves matter ──
//
// Callout D. A blank cost field on a shop floor gets a zero typed into it, and
// a zero is exactly the 79.6 defect: goods that travelled to another branch and
// arrived valued at nothing. It happened twice in one hour, the second time
// within minutes of the first being fixed.
//
// Editable because a bad run costs more than a good one and the person standing
// at the saw is the one who knows. Changing it HERE changes this run only. The
// item's set cost is changed on the second panel, which is a different act with
// a different consequence, and only some people may do it.
//
// ── Two panels in one file, deliberately ──
//
// They are the same subject — what this thing costs — reached one from the
// other, and splitting them would mean passing the item, the on-hand figure and
// the reload callback through a second component for no gain. The set-cost
// panel is also reachable from the item page, which is why it takes its own
// `visible` rather than being a mode of this one.

// THE APP HAS ONE AMBER AND ONE NEUTRAL PANEL. USE THEM.
//
// `PurchaseOrderDownload.js` carries the amber verbatim and says why in a
// comment: a first pass there "landed two or three hex points off it by
// coincidence, which is how an app ends up with four ambers nobody chose."
//
// THIS FILE WAS THE FOURTH AMBER. Its first version used #FFF7E8 / #F0DCB4 /
// #6B4A12 — three hex points off the real token, invented in this session,
// in a file written the same day as a warning against exactly that. Colby
// asked what the app already does; the answer was "not this".
const AMBER = { bg: '#FFF8EC', fg: '#8A5A08', br: '#F3D9A8' };

// The neutral panel — User Management uses it for "here is what is going on"
// text that is NOT a warning. Reused below for the not-finished-yet sentence
// under a blocked button.
//
// AMBER IS NOT THE ANSWER FOR THAT SENTENCE, and the distinction is the point:
// amber in this app means "this has a consequence you should weigh" — the
// revaluation figure, the segregation-of-duties warning. An unfinished form has
// no consequence yet. If not-finished-yet shouts as loudly as the real warning,
// the real warning stops being read.
const NEUTRAL = { bg: '#F4F6F8', fg: '#48545F', br: '#DCE2E8', bar: '#8A97A3' };

// What a blocked button's reason looks like HERE. ActionButton's own default is
// 11px grey, which reads as leftover text beside a greyed-out button — Colby,
// looking at the built sheet: "shouldnt the type how many were made be more of
// a visual trigger than just gray text?"
//
// LOCAL, VIA `reasonStyle`, NOT A CHANGE TO ActionButton ITSELF. That component
// is used on every screen in the app, and restyling its default is a design
// language decision. Colby: "put the appmwide call in #513" — recorded there.
const BLOCKED_REASON = {
  fontSize: 12,
  color: NEUTRAL.fg,
  background: NEUTRAL.bg,
  border: `1px solid ${NEUTRAL.br}`,
  borderLeft: `3px solid ${NEUTRAL.bar}`,
  borderRadius: 6,
  padding: '8px 10px',
  marginTop: 8,
};

function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function shortDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const LABEL = {
  fontSize: 12, fontWeight: 600, color: '#3A3A3A', display: 'block', marginBottom: 5,
};

// 16px minimum on any text entry — locked rule 12. Anything smaller makes iOS
// zoom the page on focus, and this is a phone-first screen.
const INPUT = {
  width: '100%', fontSize: 16, padding: '9px 10px',
  border: '1px solid #DDD', borderRadius: 8, boxSizing: 'border-box',
};

// Callout C. 44px on the phone against the app's current 32. #513 raises the
// floor app-wide at the design language gate in October; until then every
// screen touched before that raises its own, as the transfer screens did.
//
// #732, Session 79.22b.4.9 -- AND THE HEIGHT COMES FROM THE POINTER, NOT THE
// WIDTH. 44 was the standard when this was built (79.8); #417 D2 then set 48
// for a button that commits, and a tablet held at desk width got the 38s and
// 32 meant for a mouse. commitH / minH exactly as the four sibling sheets
// (RequestTransferSheet, WithdrawSheet, MakeForShelfSheet, SignOffRunSheet).
//
// THE BUTTON ROW STACKS ON A PHONE, and that is not a style preference — it is
// what `ActionButton` needs to work. It returns a FRAGMENT: the button, and
// the blocked-reason sentence as its SIBLING. Inside a flex ROW that sentence
// becomes a third flex item and renders BESIDE the button, squeezed into
// whatever is left of 375px. Found by taking a phone screenshot and reading it:
// "Type how many were made." sat next to RECORD IT instead of under it.
//
// Column on mobile, row-reverse on desktop, matching RequestTransferSheet.js
// exactly. The primary action is declared FIRST so it lands on top on a phone
// and on the right at a desk.

function Row({ label, children, strong }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      padding: '6px 0', fontSize: 13, color: strong ? '#0F0F0F' : '#3A3A3A',
      fontWeight: strong ? 700 : 500,
    }}>
      <span>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{children}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// THE PICKER — choosing what you made, when no list chose it for you.
//
// Colby, looking at the built screen: "houston will need a way to record
// production that's not tied to any specific transfer request."
//
// He was right and the truth was worse. The ONLY way in was a reserve row in
// the To make queue; a reserve row needs the item to be at or below its
// reorder point; and no Houston item has a reorder point set. HOUSTON COULD
// RECORD NOTHING AT ALL. See makeableAtBranch() in production.js for the note.
//
// SEARCHABLE BECAUSE HOUSTON MAKES 25 THINGS AND MOST SHARE THE SAME FIRST
// WORD. "Frame Std 18x24 Bot Rider", "Frame Std 18x24 Top Rider", "Frame Std
// 24x18" — a plain list of those is a wall, which is #518 one screen over. The
// box filters name and SKU together, so "24x18" and "STK" both work.
// ─────────────────────────────────────────────────────────────────────────────

export function PickWhatYouMadeSheet({ visible, onClose, onPick, isMobile, items, branchName }) {
  const [q, setQ] = useState('');

  useEffect(() => { if (visible) setQ(''); }, [visible]);
  const { coarse } = useTier();   // #732

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items || [];
    return (items || []).filter(i =>
      String(i.name || '').toLowerCase().includes(needle) ||
      String(i.sku || '').toLowerCase().includes(needle));
  }, [items, q]);

  if (!visible) return null;

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={onClose} maxWidth={440}>
      <div style={{ padding: '1.1rem 1.15rem 1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{
              fontFamily: 'var(--font-head)', textTransform: 'uppercase',
              letterSpacing: '0.04em', fontSize: 16, fontWeight: 700,
            }}>
              What did you make?
            </div>
            <div style={{ fontSize: 12, color: '#7A7A7A', marginTop: 4 }}>
              Everything {branchName || 'this branch'} makes — whether it is running low or not.
            </div>
          </div>
          <button onClick={onClose}
            aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#5C5C5C', lineHeight: 1, minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={LABEL} htmlFor="prod-pick-search">Search</label>
          <input
            id="prod-pick-search" type="text" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Name or SKU" style={INPUT} autoFocus={!isMobile}
          />
        </div>

        {(items || []).length === 0 ? (
          <div style={{ marginTop: 16, fontSize: 13, color: '#8A2F2F', lineHeight: 1.5 }}>
            Nothing is on file as made here. A branch makes something when its
            CATEGORY says so, which is set on the category rather than on the item.
          </div>
        ) : shown.length === 0 ? (
          <div style={{ marginTop: 16, fontSize: 13, color: '#5C5C5C' }}>
            Nothing matches that.
          </div>
        ) : (
          <div style={{ marginTop: 12, display: 'grid', gap: 6, maxHeight: '46vh', overflowY: 'auto' }}>
            {shown.map(i => (
              <button
                key={i.id}
                onClick={() => onPick?.(i)}
                style={{
                  textAlign: 'left', width: '100%', cursor: 'pointer',
                  background: '#fff', border: '1px solid #E9E9E9', borderRadius: 8,
                  padding: '10px 12px', minHeight: controlHeight(coarse, 'min'),
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0F0F0F' }}>{i.name}</div>
                {i.sku && <div style={{ fontSize: 11, color: '#7A7A7A' }}>{i.sku}</div>}
              </button>
            ))}
          </div>
        )}
      </div>
    </Overlay>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// PANEL 2 — Set what this costs to make. Gate screen 2, lower half.
// ─────────────────────────────────────────────────────────────────────────────

export function SetProductionCostPanel({
  visible, onClose, onSaved, isMobile, item, auth, setterName,
}) {
  const { message, type, showError, clearMessage } = useMessage();
  const [cost, setCost] = useState('');
  const [onHand, setOnHand] = useState(null);
  const [busy, setBusy] = useState(false);
  // Who last set this figure, for the lock. See mayChangeThisCost().
  const [setter, setSetter] = useState(null);

  useEffect(() => {
    if (!visible || !item) return;
    setCost(item.standard_cost == null ? '' : String(item.standard_cost));
    clearMessage();
    let alive = true;
    // Across EVERY branch, not just this one. The cost is a property of the
    // item (gate D32, one column), so a revaluation figure naming one branch's
    // shelf would understate the change on the very screen that exists to state
    // it in full.
    totalOnHand(item.id).then(r => { if (alive) setOnHand(r.ok ? r.qty : null); });
    setSetter(null);
    costSetter(item.standard_cost_set_by).then(u => { if (alive) setSetter(u); });
    return () => { alive = false; };
    // A bare disable, NOT `eslint-disable-next-line react-hooks/...`. This
    // project ships no ESLint config, so naming a React rule is itself a FATAL
    // BUILD ERROR — BatchPrintModal.js already says so in a comment, and this
    // file hit it anyway. The suite stayed green throughout: no test imports
    // this page, and a build error is not a test failure.
  }, [visible, item?.id]); // eslint-disable-line

  const change = useMemo(
    () => revaluation({ qtyOnHand: onHand, oldCost: item?.standard_cost, newCost: cost }),
    [onHand, item, cost]);
  const { coarse } = useTier();   // #732
  const commitH = controlHeight(coarse, 'commit');
  const minH = controlHeight(coarse, 'min');

  async function save() {
    setBusy(true);
    const r = await setStandardCost({ itemId: item.id, cost, setBy: auth?.profile?.id });
    setBusy(false);
    if (r.error) { showError(r.error.message); return; }
    onSaved?.(r.item);
    onClose?.();
  }

  if (!visible || !item) return null;

  const lastSet = shortDate(item.standard_cost_set_at);

  // #626, Session 79.22b.1 -- THE AUTHOR LINE READ FROM A PROP NOBODY PASSES.
  //
  // `setterName` is threaded from RecordProductionSheet into this panel and is
  // supplied by NO caller anywhere in the app - checked by grep, not assumed.
  // So the line fell through to "not set by anyone yet, it came across from the
  // old vendor price" for EVERY item on EVERY screen, including one an
  // executive had just priced. #626 saw it as the item page's missing column;
  // it is that AND this, and fixing only the column would have left the
  // sentence exactly as wrong.
  //
  // The name comes from the lookup this panel already does for the lock, so
  // there is one source and no prop for a future caller to forget. `setterName`
  // stays as an override rather than being deleted: it costs nothing and
  // removing a prop threaded through two components is churn this does not need.
  const authorName = setterName || setter?.full_name || null;
  // AND A FIGURE THAT HAS AN AUTHOR MUST NOT CLAIM IT HAS NONE WHILE THEIR NAME
  // IS STILL IN FLIGHT. costSetter() is a round trip; for as long as it takes,
  // `setter` is null, and the old fallback would have asserted nobody set a
  // figure somebody did. The column is the fact; the name is the decoration.
  const hasAuthor = !!(setterName || item.standard_cost_set_by);

  // Colby, Session 79.8: "once an executive sets cost to make then even a
  // manager shouldn't be able to override without clearence."
  //
  // THE MESSAGE NAMES A PERSON, NOT A RULE, and that is the whole reason this
  // looks up the setter rather than just refusing. "You cannot change this" is
  // a dead end; "Angie set this on Sep 8 — ask her" is a next step. The
  // database refuses this independently, so the screen's job here is to say
  // who to ask rather than to be the guard.
  const { allowed: mayChange, lockedBy } = mayChangeThisCost({ auth, setter });

  const blocked = !mayChange
    ? (lockedBy
        ? `${lockedBy.full_name || 'An executive'} set this figure. Only an executive or an administrator can change it.`
        : 'You are not set up to change what an item costs to make.')
    : cost === '' || cost == null ? 'Enter what it costs to make one.'
      : Number(cost) < 0 ? 'A cost cannot be negative.'
        : null;

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={busy ? undefined : onClose} maxWidth={440}>
      <div style={{ padding: '1.1rem 1.15rem 1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{
              fontFamily: 'var(--font-head)', textTransform: 'uppercase',
              letterSpacing: '0.04em', fontSize: 16, fontWeight: 700,
            }}>
              Set what this costs to make
            </div>
            <div style={{ fontSize: 13, color: '#0F0F0F', marginTop: 4, fontWeight: 600 }}>{item.name}</div>
          </div>
          <button onClick={onClose} disabled={busy}
            aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#5C5C5C', lineHeight: 1, minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        <InlineMessage type={type} message={message} onDismiss={clearMessage} />

        <div style={{ marginTop: 14 }}>
          <label style={LABEL} htmlFor="std-cost">Cost to make, each</label>
          <input
            id="std-cost" type="number" inputMode="decimal" min="0" step="0.01"
            value={cost} onChange={e => setCost(e.target.value)}
            readOnly={!mayChange}
            style={{ ...INPUT, ...(mayChange ? {} : { background: '#F7F7F7', color: '#7A7A7A' }) }}
          />
          {/* The author line. An item whose cost came across from the old vendor
              price deliberately has NO author — saying "not set by anyone yet"
              is honest, and naming somebody who did not set it is how an audit
              trail starts lying. */}
          <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 5 }}>
            {item.standard_cost == null
              ? 'Not set yet.'
              : authorName && lastSet ? `Last changed ${lastSet} · ${authorName}`
                : hasAuthor
                  ? (lastSet ? `Last changed ${lastSet}` : 'Set, but we have no record of when.')
                  : lastSet ? `Last changed ${lastSet} — not set by anyone yet, it came across from the old vendor price`
                    : 'Set, but we have no record of when.'}
          </div>
        </div>

        {/* Callout F. STATED IN DOLLARS, NOT IN PRINCIPLE. "This will revalue
            inventory" is not something the person pressing Save can check.
            "312 on hand goes from $1,154.40 to $1,279.20" is. */}
        {change && (
          <div style={{
            marginTop: 14, background: AMBER.bg, border: `1px solid ${AMBER.br}`,
            borderRadius: 8, padding: '10px 12px', fontSize: 13, color: AMBER.fg,
          }}>
            Changing this revalues what is already on the shelf.{' '}
            <strong>{change.qtyOnHand}</strong> on hand{' '}
            {change.hadNoCost ? 'is worth nothing today and becomes' : `goes from ${money(change.before)} to`}{' '}
            <strong>{money(change.after)}</strong> — a change of{' '}
            <strong>{change.delta > 0 ? '+' : ''}{money(change.delta)}</strong> the moment you save.
          </div>
        )}

        <div style={{ marginTop: 12, fontSize: 12, color: '#7A7A7A' }}>
          This is what it costs <em>us</em> to make, never what we sell it for. Stock is carried at cost.
        </div>

        <div style={{
          display: 'flex', flexDirection: isMobile ? 'column' : 'row-reverse',
          justifyContent: isMobile ? undefined : 'flex-start',
          alignItems: isMobile ? 'stretch' : 'center', gap: 10, marginTop: 18,
        }}>
          <ActionButton
            onClick={save} blockedReason={blocked} reasonStyle={BLOCKED_REASON} busy={busy} busyLabel="Saving..."
            className="btn btn-primary"
            style={isMobile
              ? { width: '100%', justifyContent: 'center', minHeight: commitH }
              : { minWidth: 150, justifyContent: 'center', minHeight: commitH }}
          >
            Save
          </ActionButton>
          <button onClick={onClose} disabled={busy}
            className="btn btn-ghost"
            style={isMobile
              ? { width: '100%', justifyContent: 'center', minHeight: minH }
              : { minWidth: 110, justifyContent: 'center', minHeight: minH }}>
            Cancel
          </button>
        </div>
      </div>
    </Overlay>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PANEL 1 — Record a run. Gate screen 2, upper half.
// ─────────────────────────────────────────────────────────────────────────────

export default function RecordProductionSheet({
  visible, onClose, onDone, isMobile, item, branchName, qtyOnHand, auth, setterName,
  // #580 part (a), gate 553 D27 -- where the caller can open the sign-off
  // sheet, the pointer below carries a button to it. Optional: the item page
  // has no sign-off sheet, and there the sentence alone is the pointer.
  onSignOffInstead = null,
}) {
  const { message, type, showError, clearMessage } = useMessage();
  const [qty, setQty] = useState('');
  const [cost, setCost] = useState('');
  const [busy, setBusy] = useState(false);
  const [costPanel, setCostPanel] = useState(false);
  // D27 -- a confirmed request for our own shelf that still wants this item.
  const [openRequest, setOpenRequest] = useState(null);

  // RECORDING A RUN NOBODY ASKED FOR STAYS EXACTLY AS IT IS (D27, and Colby at
  // 79.8: "houston will need a way to record production that's not tied to any
  // specific transfer request"). But with a request open, 14 frames recorded
  // through this door land on the shelf while the request goes on saying 14
  // still to make: the work is done twice, or the request sits open forever.
  // So the sheet POINTS at the request rather than blocking the run.
  //
  // Looked up here rather than passed in, because three screens open this
  // sheet and each would otherwise carry its own copy of the lookup. Only a
  // CONFIRMED request can be signed off against; a failed lookup shows no
  // pointer and the run still records, because the pointer is advice.
  const myBranchId = auth?.profile?.branch_id ?? null;
  useEffect(() => {
    if (!visible || item?.id == null || myBranchId == null) { setOpenRequest(null); return undefined; }
    let live = true;
    supabase
      .from('branch_transfers')
      .select('id, transfer_number, status, from_branch_id, to_branch_id, fulfilment_mode, branch_transfer_items(item_id, qty_requested, qty_ready, qty_shipped, qty_received, line_status)')
      .eq('from_branch_id', myBranchId)
      .eq('to_branch_id', myBranchId)
      .eq('status', 'approved')
      .order('requested_at', { ascending: true })
      .then(({ data }) => {
        if (!live) return;
        const hit = (data || []).find(t =>
          (t.branch_transfer_items || []).some(l => String(l.item_id) === String(item.id) && l.line_status !== 'refused')
          && makeProgress((t.branch_transfer_items || []).filter(l => String(l.item_id) === String(item.id))).toMake > 0);
        const line = hit && hit.branch_transfer_items.find(l => String(l.item_id) === String(item.id));
        setOpenRequest(hit ? { transfer: hit, qty: Number(line?.qty_requested) || 0 } : null);
      });
    return () => { live = false; };
  }, [visible, item?.id, myBranchId]);

  useEffect(() => {
    if (!visible) return;
    setQty('');
    // Callout D — pre-filled, never blank. `RequestTransferSheet` had no reset
    // of any kind and that was #516: the sheet kept the last item's answers,
    // because returning null from a render does NOT unmount the component.
    setCost(item?.standard_cost == null ? '' : String(item.standard_cost));
    clearMessage();
  }, [visible, item?.id, item?.standard_cost]); // eslint-disable-line
  const { coarse } = useTier();   // #732
  const commitH = controlHeight(coarse, 'commit');
  const minH = controlHeight(coarse, 'min');

  const made = Number(qty);
  const after = Number.isFinite(made) && qty !== '' ? (Number(qtyOnHand) || 0) + made : null;
  const added = valueAdded({ qty, unitCost: cost });
  const maySeeCost = canSeeInventoryCost(auth || {});
  const maySetCost = canSetProductionCost(auth);

  async function submit() {
    setBusy(true);
    const r = await recordProduction({
      itemId: item.id,
      qty,
      // Never send a cost the person was not shown. For somebody who cannot
      // see pricing this is always null, and the database fills it in from
      // what the item is set at.
      unitCost: maySeeCost ? cost : null,
      notes: null,
    });
    setBusy(false);
    if (r.error) { showError(r.error.message); return; }
    onDone?.(r);
    onClose?.();
  }

  if (!visible || !item) return null;

  const blocked = qty === '' ? 'Type how many were made.'
    : !(made > 0) ? 'How many were made? It has to be more than zero.'
      : null;

  return (
    <>
      <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={busy ? undefined : onClose} maxWidth={440}>
        <div style={{ padding: '1.1rem 1.15rem 1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div>
              <div style={{
                fontFamily: 'var(--font-head)', textTransform: 'uppercase',
                letterSpacing: '0.04em', fontSize: 16, fontWeight: 700,
              }}>
                Record production
              </div>
              <div style={{ fontSize: 13, color: '#0F0F0F', marginTop: 4, fontWeight: 600 }}>
                {item.name}{branchName ? ` · ${branchName}` : ''}
              </div>
              {item.sku && <div style={{ fontSize: 11, color: '#7A7A7A' }}>{item.sku}</div>}
            </div>
            <button onClick={onClose} disabled={busy}
              aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#5C5C5C', lineHeight: 1, minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          </div>

          <InlineMessage type={type} message={message} onDismiss={clearMessage} />

          {openRequest && (
            <div role="status" style={{
              marginTop: 12, background: AMBER.bg, border: `1px solid ${AMBER.br}`,
              borderRadius: 8, padding: '10px 12px', fontSize: 13, color: AMBER.fg, lineHeight: 1.5,
            }}>
              A request for {openRequest.qty} of these is open on {openRequest.transfer.transfer_number || 'a request for our own shelf'}. Sign off against it instead, so it counts.
              {onSignOffInstead && (
                <div style={{ marginTop: 8 }}>
                  <button type="button" className="btn btn-secondary"
                    onClick={() => onSignOffInstead(openRequest.transfer)}
                    style={{ justifyContent: 'center', minHeight: minH }}>
                    Sign off against it
                  </button>
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <label style={LABEL} htmlFor="prod-qty">How many</label>
            <input
              id="prod-qty" type="number" inputMode="decimal" min="0"
              value={qty} onChange={e => setQty(e.target.value)} style={INPUT}
              autoFocus={!isMobile}
            />
          </div>

          {/* THE COST FIELD IS GATED, AND ITS ABSENCE IS THE FIX FOR A DEFECT
              COLBY FOUND BY LOOKING AT THIS SHEET.

              His question: "so even a team member can adjust the cost to make
              of every frame? how is that possible if a team member cant see
              pricing?" He was right, and it was worse than the link he was
              looking at. The "Change what it is set to" link WAS gated to
              manager and above. THIS FIELD WAS GATED TO NOTHING — a team
              member saw the cost, pre-filled, and could type any figure over
              it, which is written onto the ledger row the goods travel with.

              `canSeeInventoryCost` deliberately excludes a team member, and
              #77 depends on that holding. This sheet walked straight through
              it.

              A TEAM MEMBER STILL RECORDS THE RUN — Colby's ruling, and it is
              the same reasoning as gate D35: the person who cut the steel is
              the person who knows how many. They see "How many" and nothing
              else, and the COST COMES FROM THE ITEM, filled in by the database
              through `COALESCE(p_unit_cost, standard_cost)` — machinery that
              already existed for exactly this. Nobody who cannot see pricing
              sets pricing, and nobody is stopped from recording their work. */}
          {maySeeCost && (
          <div style={{ marginTop: 14 }}>
            <label style={LABEL} htmlFor="prod-cost">Cost each</label>
            <input
              id="prod-cost" type="number" inputMode="decimal" min="0" step="0.01"
              value={cost} onChange={e => setCost(e.target.value)} style={INPUT}
            />
            <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 5 }}>
              {item.standard_cost == null
                ? 'Nothing is set for this item yet. What you type here is used for this run only.'
                : 'This is the set cost for this item. You may change it for this run.'}
            </div>
            {/* Callout G — only somebody with the switch on sees this. Everyone
                else sees the cost and cannot change what it is set to.

                #732, Session 79.22b.4.9 -- ON ITS OWN LINE, because inside the
                11px sentence it was as tall as its text: under even the 24px
                mouse floor. Colby approved the before-and-after picture. */}
            {maySetCost && (
              <button
                type="button"
                onClick={() => setCostPanel(true)}
                style={{
                  display: 'flex', alignItems: 'center', minHeight: minH,
                  background: 'none', border: 'none', padding: 0, fontFamily: 'inherit', fontSize: 13,
                  color: 'var(--green, #1D9E75)', textDecoration: 'underline', cursor: 'pointer',
                }}
              >
                Change what it is set to
              </button>
            )}
          </div>
          )}

          {/* NO COST SET, AND A RUN ABOUT TO BE RECORDED WITHOUT ONE.
              This is the 79.6 defect arriving by a new route: the sheet
              pre-fills the cost from the item, and an item nobody has priced
              has nothing to pre-fill, so the ledger row would carry null and
              anything later sent to another branch would arrive worth nothing.

              WARNED RATHER THAN BLOCKED, deliberately, and the gate's
              acceptance line P2 ("it carries a cost, not null, not zero") is
              not satisfied literally here. Three reasons:

                * Refusing to record a run that really happened leaves the
                  STOCK COUNT wrong, and a wrong count is worse than an
                  unvalued row — the count is what people work from.
                * Requiring a number from somebody who does not know one
                  invites a zero, which is exactly what callout D warns about:
                  a zero is a claim the thing was free, while null says nobody
                  has said yet. The 79.6 defect was a zero, twice.
                * The person who can fix it properly is offered the way to,
                  one line up, if they hold the switch.

              Raised with Colby at 79.8 rather than decided quietly. */}
          {maySeeCost && made > 0 && cost === '' && (
            <div style={{
              marginTop: 14, background: AMBER.bg, border: `1px solid ${AMBER.br}`,
              borderRadius: 8, padding: '10px 12px', fontSize: 13, color: AMBER.fg, lineHeight: 1.5,
            }}>
              <strong>Nothing says what this costs to make.</strong> These {made} will go on the
              rack worth nothing, and anything sent to another branch will arrive valued at
              nothing too.{maySetCost
                ? ' Set it above and this run picks the figure up.'
                : ' Recording is still right — ask a manager to set what it costs.'}
            </div>
          )}

          <div style={{ marginTop: 16, borderTop: '1px solid #EEE', paddingTop: 8 }}>
            <Row label="On hand now">{Number(qtyOnHand) || 0}</Row>
            <Row label="After this run">{after == null ? '—' : after}</Row>
            {/* Callout E. Shown BEFORE saving. A person recording 300 stakes
                should see $1,230 become a number they can sanity-check, not
                discover it on a report next week.

                HIDDEN ENTIRELY from somebody who cannot see pricing. For them
                it can only ever read "—", and a row that can never say
                anything reads as broken rather than as withheld. The two
                counts above it are quantities, which they may see. */}
            {maySeeCost && (
              <Row label="Value added" strong>{added == null ? '—' : money(added)}</Row>
            )}
          </div>

          <div style={{
            display: 'flex', flexDirection: isMobile ? 'column' : 'row-reverse',
            justifyContent: isMobile ? undefined : 'flex-start',
            alignItems: isMobile ? 'stretch' : 'center', gap: 10, marginTop: 18,
          }}>
            <ActionButton
              onClick={submit} blockedReason={blocked} reasonStyle={BLOCKED_REASON} busy={busy} busyLabel="Recording..."
              className="btn btn-primary"
              style={isMobile
                ? { width: '100%', justifyContent: 'center', minHeight: commitH }
                : { minWidth: 150, justifyContent: 'center', minHeight: commitH }}
            >
              Record it
            </ActionButton>
            <button onClick={onClose} disabled={busy}
              className="btn btn-ghost"
              style={isMobile
                ? { width: '100%', justifyContent: 'center', minHeight: minH }
                : { minWidth: 110, justifyContent: 'center', minHeight: minH }}>
              Cancel
            </button>
          </div>
        </div>
      </Overlay>

      <SetProductionCostPanel
        visible={costPanel}
        onClose={() => setCostPanel(false)}
        onSaved={saved => {
          // Carry the new figure straight into the run in progress, so the
          // person does not have to close and reopen to use what they just set.
          if (saved?.standard_cost != null) setCost(String(saved.standard_cost));
          onDone?.({ costOnly: true, item: saved });
        }}
        isMobile={isMobile}
        item={item}
        auth={auth}
        setterName={setterName}
      />
    </>
  );
}
